import { inject, Injectable } from '@angular/core';
import { FeathersService } from './feathers.service';
import {
  BehaviorSubject,
  concatMap,
  defer,
  delay,
  from,
  Observable,
  of,
  retry,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ApiEndpoint, LocalStorageKey } from '../../enums';
import { TagRequest, TagResponse } from '../../models/tag';
import { LocalStorageService } from '../local-storage.service';
import { DialogService } from '../dialog.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class TagsService {
  feathers = inject(FeathersService);
  localStorageService = inject(LocalStorageService);
  dialog = inject(DialogService);
  translateService = inject(TranslateService);

  private readonly nextPendingTagId$ = new BehaviorSubject<string | null>(
    this.localStorageService.get(LocalStorageKey.PendingTagId)
  );
  public readonly pendingTagId$ = this.nextPendingTagId$.pipe(
    tap((tagId) => {
      if (tagId) {
        this.localStorageService.set(LocalStorageKey.PendingTagId, tagId);
      } else {
        this.localStorageService.delete(LocalStorageKey.PendingTagId);
      }
    }),
    shareReplay(1)
  );

  readTag(
    tagRequest: TagRequest,
    showErrNotification = true
  ): Observable<TagResponse> {
    const expectedDurationMS = 2000;
    let callStart: number;

    return of(null).pipe(
      tap(() => (callStart = Date.now())),
      switchMap(() =>
        defer(() =>
          this.feathers.post<TagResponse>(
            ApiEndpoint.Tags,
            tagRequest,
            showErrNotification
          )
        ).pipe(
          tap({
            next: () =>
              this.localStorageService.deletePendingTagScan(tagRequest),
            error: () =>
              this.localStorageService.savePendingTagScan(tagRequest),
          }),
          switchMap((res) => {
            const timeDiff = Date.now() - callStart;
            const timeLeft = expectedDurationMS - timeDiff;
            return of(res).pipe(delay(timeLeft >= 0 ? timeLeft : 0));
          })
        )
      )
    );
  }

  executePendingTagScans() {
    return from(this.localStorageService.pendingTagScans).pipe(
      concatMap((pendingTagScan) =>
        this.readTag(pendingTagScan, false).pipe(
          retry({ count: 5, delay: 5 * 60000 })
        )
      )
    );
  }

  setNewPendingTagId(tagId: string) {
    return this.pendingTagId$.pipe(
      take(1),
      switchMap((currentPendingTagId) => {
        return currentPendingTagId && currentPendingTagId !== tagId
          ? defer(
              () =>
                this.dialog.open({
                  title: this.translateService.instant(
                    'dialogs.replaceCurrentPendingTagId.title'
                  ),
                  text: this.translateService.instant(
                    'dialogs.replaceCurrentPendingTagId.text'
                  ),
                  confirmButtonText: this.translateService.instant(
                    'dialogs.replaceCurrentPendingTagId.confirmButtonText'
                  ),
                  rejectButtonText: this.translateService.instant(
                    'dialogs.replaceCurrentPendingTagId.rejectButtonText'
                  ),
                }).result
            )
          : of(true);
      }),
      tap((res) => {
        if (res) {
          this.nextPendingTagId$.next(tagId);
        }
      })
    );
  }

  discardPendingTagId() {
    this.pendingTagId$
      .pipe(
        take(1),
        switchMap((pendingTagId) =>
          pendingTagId
            ? this.dialog.open({
                title: this.translateService.instant(
                  'dialogs.discardCurrentPendingTagId.title'
                ),
                text: this.translateService.instant(
                  'dialogs.discardCurrentPendingTagId.text'
                ),
                confirmButtonText: this.translateService.instant(
                  'dialogs.discardCurrentPendingTagId.confirmButtonText'
                ),
                rejectButtonText: this.translateService.instant(
                  'dialogs.discardCurrentPendingTagId.rejectButtonText'
                ),
              }).result
            : of(true)
        )
      )
      .subscribe((res) => {
        if (res) {
          this.nextPendingTagId$.next(null);
        }
      });
  }

  clearPendingTagId() {
    this.nextPendingTagId$.next(null);
  }
}
